import api from '../utils/api';

/**
 * Service for property-related API requests
 */
const propertyService = {
  /**
   * Get all properties with optional filtering
   * @param {object} params - Filter parameters
   * @return {Promise} - API response
   */
  getProperties: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/properties${queryParams ? `?${queryParams}` : ''}`);
  },
  
  /**
   * Get a specific property by ID
   * @param {string} id - Property ID
   * @return {Promise} - API response
   */
  getPropertyById: (id) => {
    return api.get(`/properties/${id}`);
  },
  
  /**
   * Get property details with valuation
   * @param {string} id - Property ID
   * @return {Promise} - API response
   */
  getPropertyValuation: (id) => {
    return api.get(`/properties/${id}/valuation`);
  },
  
  /**
   * Get Zillow data for a property
   * @param {string} id - Property ID
   * @return {Promise} - API response
   */
  getZillowData: (id) => {
    return api.get(`/properties/${id}/zillow`);
  },
  
  /**
   * Search properties by location
   * @param {string} location - Location to search (city, zip, etc.)
   * @return {Promise} - API response
   */
  searchByLocation: (location) => {
    return api.get(`/properties/search?location=${encodeURIComponent(location)}`);
  },
  
  /**
   * Get property analytics
   * @param {string} id - Property ID
   * @return {Promise} - API response
   */
  getPropertyAnalytics: (id) => {
    return api.get(`/properties/${id}/analytics`);
  },

  /**
   * Get property valuation by address
   * @param {string} address - Property address
   * @param {string} city - Property city
   * @param {string} state - Property state
   * @return {Promise} - API response
   */
  getPropertyValuationByAddress: async (address, city, state) => {
    try {
      const response = await api.get('/property/valuation', {
        params: { address, city, state }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching property valuation:', error);
      throw error;
    }
  },

  /**
   * Get property details by ID
   * @param {string} id - Property ID
   * @return {Promise} - API response
   */
  getPropertyDetails: async (id) => {
    try {
      const response = await api.get(`/property/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching property details:', error);
      throw error;
    }
  },

  /**
   * Get bulk cached property data for multiple properties
   * @param {Array} propertyIds - Array of property IDs
   * @return {Promise} - API response with cached data
   */
  getBulkCachedPropertyData: async (propertyIds) => {
    try {
      const response = await api.post('/properties/bulk-cached-data', {
        propertyIds: propertyIds
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching bulk cached data:', error);
      throw error;
    }
  },

  /**
   * Get cached property data for a single property
   * @param {string} propertyId - Property ID
   * @return {Promise} - API response with cached data
   */
  getCachedPropertyData: async (propertyId) => {
    try {
      const response = await api.get(`/properties/${propertyId}/cached-data`);
      return response.data;
    } catch (error) {
      console.error('Error fetching cached property data:', error);
      return null; // Return null if no cached data
    }
  }
};

export default propertyService; 